import { formatISO } from 'date-fns';
import gql from 'graphql-tag';
import { useMemo } from 'react';
import { useQuery } from 'urql';

import { RangeDate } from '@/globalTypes';
import { useQueryParams, useRgVersion } from '@/hooks';
import { mapVariables } from '@/utils';
import { makeOrderBy, makePagination } from '@/utils/pagination';
import { PlayerRgRiskAssessmentHistoryQuery } from './__generated__/usePlayerRgRiskAssessmentHistory';
import { queryParams } from './queryParams';

const QUERY = gql`
  query PlayerRgRiskAssessmentHistory(
    $rgCraEventId: String
    $playerGlobalId: ID
    $eventType: RgCraV2EventType
    $rgCraOutcomes: [RgCraV2OutcomeEnum!]
    $comment: String
    $initiatorId: String
    $initiatorType: InitiatorTypeEnum
    $rgCraProcessedAt: RangeDate
    $orderBy: RgCraV2RiskHistoryOrderBy
    $desc: Boolean
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    viewer {
      id
      rgRiskAssessmentHistory: rgCraV2RiskAssessmentHistory(
        rgCraEventId: $rgCraEventId
        playerGlobalId: $playerGlobalId
        rgCraEventType: $eventType
        rgCraOutcomes: $rgCraOutcomes
        comment: $comment
        initiatorId: $initiatorId
        initiatorType: $initiatorType
        rgCraProcessedAt: $rgCraProcessedAt
        rgCraHistoryOrderBy: $orderBy
        desc: $desc
        before: $before
        after: $after
        first: $first
        last: $last
      ) {
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
        edges {
          node {
            eventId
            playerGlobalId
            playerId
            eventType
            comment
            initiator {
              ... on PlayerInitiator {
                player {
                  firstName
                }
              }
              ... on AgentInitiator {
                agent {
                  agentId
                  email
                  firstName
                  lastName
                }
              }
            }
            outcomes {
              name
              comment
              ... on RgCraV2RiskLevelUpdated {
                explanation
              }
              __typename
            }
            processedAt
          }
        }
      }
    }
  }
`;

const LEGACY_QUERY = gql`
  query PlayerRgRiskAssessmentHistoryLegacy(
    $rgCraEventId: String
    $playerGlobalId: ID
    $eventType: RgCraEventType
    $rgCraOutcomes: [RgCraOutcomeEnum!]
    $comment: String
    $initiatorId: String
    $initiatorType: InitiatorTypeEnum
    $rgCraProcessedAt: RangeDate
    $orderBy: RgCraRiskHistoryOrderBy
    $desc: Boolean
    $before: String
    $after: String
    $first: Int
    $last: Int
  ) {
    viewer {
      id
      rgRiskAssessmentHistory: rgCraRiskAssessmentHistory(
        rgCraEventId: $rgCraEventId
        playerGlobalId: $playerGlobalId
        rgCraEventType: $eventType
        rgCraOutcomes: $rgCraOutcomes
        comment: $comment
        initiatorId: $initiatorId
        initiatorType: $initiatorType
        rgCraProcessedAt: $rgCraProcessedAt
        rgCraHistoryOrderBy: $orderBy
        desc: $desc
        before: $before
        after: $after
        first: $first
        last: $last
      ) {
        pageInfo {
          endCursor
          hasNextPage
          startCursor
          hasPreviousPage
        }
        edges {
          node {
            eventId
            playerGlobalId
            playerId
            eventType
            comment
            initiator {
              ... on PlayerInitiator {
                player {
                  firstName
                }
              }
              ... on AgentInitiator {
                agent {
                  agentId
                  email
                  firstName
                  lastName
                }
              }
            }
            processedAt
          }
        }
      }
    }
  }
`;

export default function usePlayerRiskAssessment(playerGlobalId: string) {
  const { isLegacy, toggleVersion } = useRgVersion();
  const [query, setQuery] = useQueryParams(queryParams);

  const rgCraProcessedAt = useMemo((): RangeDate | undefined => {
    if (query.from && query.to) {
      return {
        from: formatISO(query.from),
        to: formatISO(query.to),
      };
    }

    return undefined;
  }, [query.from, query.to]);

  const { ...queryVariables } = query;

  const [{ data, fetching }, refresh] =
    useQuery<PlayerRgRiskAssessmentHistoryQuery>({
      query: isLegacy ? LEGACY_QUERY : QUERY,
      variables: {
        playerGlobalId,
        ...mapVariables(queryVariables),
        rgCraProcessedAt,
      },
    });

  return {
    refresh,
    fetching,
    isLegacy,
    toggleVersion,
    defaultFilter: query,
    updateFilter: (values: {}) => setQuery(values, 'replaceIn'),
    ...makeOrderBy(query, setQuery),
    ...makePagination(
      query,
      setQuery,
      data?.viewer.rgRiskAssessmentHistory.pageInfo,
    ),
    history: data?.viewer.rgRiskAssessmentHistory?.edges?.map(
      (edge) => edge?.node,
    ),
  };
}
